import React from 'react';
import {t} from '@lingui/macro';
import dayjs from 'dayjs';
import TransactionLink from 'bukku/components/TransactionLink';
import AmountTd from "bukku/components/Table/Common/AmountTd";
import {Type} from "bukku/redux/Transaction/helper";
import {store} from "bukku/redux/store";
import TransactionType from '../../../../../components/TransactionType';
import { StorageKey } from '../../../../../enums/StorageKey';
import classNames from 'classnames';

export const reportTitles = () => ({
    [Type.sale_quote]: t`Quotations by Customer`,
    [Type.sale_order]: t`Sales Orders by Customer`,
    [Type.sale_delivery_order]: t`Delivery Orders by Customer`,
    [Type.purchase_order]: t`Purchase Orders by Supplier`,
});
export const reportTypes = {
    [Type.sale_quote]: 'customer_quotes',
    [Type.sale_order]: 'sales_orders_by_customer',
    [Type.sale_delivery_order]: 'delivery_orders_by_customer',
    [Type.purchase_order]: 'supplier_orders',
};
export const isCustomer = {
    [Type.sale_quote]: true,
    [Type.sale_order]: true,
    [Type.sale_delivery_order]: true,
    [Type.purchase_order]: false,
};

export const getTransactions = () => {
    const { Report } = store.getState();
    const { reportData } = Report;

    // spacer
    const spacerLine = () => ({
        key: Math.random(),
        colType: 'spacer',
    });

    const transactions = [];
    reportData.report.contacts.forEach(contact => {
        transactions.push({
            key: `heading_${contact.id}`,
            colType: 'heading',
            label: `${contact.legal_name} (${contact.contact_code})`,
        });

        // Transactions
        contact.transactions.forEach(transaction => {
            transactions.push({
                key: `${contact.id}_${transaction.id}_${Math.random()}`,
                ...transaction,
            });
        });

        // Contact total
        transactions.push({
            // USED IN ALL
            key: `total_${contact.id}`,
            colType: 'subtotal',
            // currency_symbol: contact.currency_symbol,
            label: t`Total`,
            amount: contact.amount,
            balance_quantity: contact.balance_quantity,
            base_amount: contact.base_amount,
            // USED IN SO AND PO
            quantity: contact.total_quantity,
            // USED IN DO AND QUOTATION
            total_quantity: contact.total_quantity,
        });
        transactions.push(spacerLine());
    });

    const grandTotals = reportData.report?.grand_total ?? {};
    transactions.push({
        key: 'grand_total',
        colType: 'grand_total',
        label: t`Grand Total`,

        amount: grandTotals?.amount,
        base_amount: grandTotals?.base_amount,
        quantity: grandTotals?.total_quantity, // for SO and PO report
        total_quantity: grandTotals?.total_quantity, // for DO report
        balance_quantity: grandTotals?.balance_quantity,
    });

    // DO by customer
    // total_quantity, balance_quantity and base_amount

    // SO by customer + PO by supplier
    // quantity, balance_quantity and base_amount [NO amount]

    // Quotation by customer
    // only amount
    return transactions;
};

// cellData is based on column key. Used for number, description, quantity
export const renderTransactionType = ({ rowData }) => (
    <div className="BaseTable__row-cell-text">
        <TransactionType record={rowData} />
    </div>
);

// cellData is based on column key. Used for number, description, quantity
export const renderCellText = ({ cellData }) => (
    <div className="BaseTable__row-cell-text">
        {cellData || '-'}
    </div>
);

export const renderDate = ({ rowData }) => {
    const label = dayjs(rowData.date).format('DD/MM/YYYY');
    return (
        <div className="indent-col">
            <TransactionLink transaction={rowData} label={label} />
        </div>
    );
};

export const renderStatus = ({ rowData }) => {
    switch (rowData.transfer_status) {
        case 'TRANSFERRED':
            return t`Transferred`;
        case 'PARTIAL_TRANSFERRED':
            return t`Partially Transferred`;
        case "NOT_TRANSFERRED":
            return t`Not Transferred`;
        default:
            return '-';
    }
};

// amount for SO and PO report is slightly different than Quotation and DO report
export const renderAmountSoPo = ({ cellData, column, rowData }) => {
    const { Report } = store.getState();
    const { reportData } = Report;

    if (cellData === '' || cellData === undefined || cellData === null)
        return null;

    switch (column.key) {
        case 'amount':
            return <AmountTd
                currency_symbol={rowData.currency_symbol}
                amount={cellData}
                className="amount-td"
            />;
        case 'base_amount':
            return <AmountTd
                currency_symbol={reportData.company.currency_symbol}
                amount={cellData}
                className="amount-td"
            />;
        case 'quantity':
        case 'balance_quantity':
        default:
            return <AmountTd amount={cellData} />;
    }
};

// amount can be balance, amount. Will have Currency
export const renderAmount = ({ cellData, column }) => {
    const { Report } = store.getState();
    const { reportData } = Report;

    if (cellData === '' || cellData === undefined || cellData === null)
        return null;

    switch (column.key) {
        case 'amount':
        case 'base_amount':
            return <AmountTd currency_symbol={reportData.company.currency_symbol} amount={cellData} />;
        case 'total_quantity':
        case 'balance_quantity':
        default:
            return <AmountTd amount={cellData} />;
    }
};

export const renderCreatedAt = ({ cellData }) => {
    if (cellData === '' || cellData === undefined || cellData === null)
        return '-';
    return dayjs(cellData).format('DD/MM/YYYY hh:mm a');
};

export const rowRenderer = (props, storageKey = null) => {
    const {
        cells,
        rowData,
        columns,
    } = props;

    // Override
    let className;
    let children;

    switch (rowData.colType) {
        case 'spacer':
            className = `${cells[0].props.className} spacer-col`;
            children = '';
            return React.cloneElement(cells[0], { className, children });
        case 'heading':
            className = `${cells[0].props.className} heading-col`;
            children = rowData.label;
            return React.cloneElement(cells[0], { className, children });
        case 'subtotal':
        case 'grand_total':
            return cells.map((cell, cellIdx) => {
                const {key: cellKey} = columns[cellIdx];
                const omittedCols = [
                    'number', 'type', 'number2', 'description', 'title', 'transfer_status', 'created_at',
                ];
                if (
                    [StorageKey.report_sale_order_by_customer, StorageKey.report_purchase_order_by_supplier]
                        .includes(storageKey)
                )
                    omittedCols.push('amount');

                className = classNames(
                    cells[cellIdx].props.className,
                    rowData.colType === 'grand_total' ? 'total-col' : 'subtotal-col',
                );

                if (cellIdx === 0) {
                    const childrenClassName = classNames(
                        {
                            'indent-col': rowData.colType === 'subtotal',

                        },
                    );
                    children = (
                        <div className={childrenClassName}>
                            {rowData.label}
                        </div>
                    );
                    return React.cloneElement(cells[cellIdx], { className, children });
                }

                if (omittedCols.includes(cellKey)) {
                    children = '';
                    return React.cloneElement(cells[cellIdx], { className, children });
                }
                return React.cloneElement(cells[cellIdx], { className });
            });
        default:
            return cells;
    }
};
